import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, IconButton } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import MailIcon from '@mui/icons-material/MailOutline';

import { useTranslation } from 'react-i18next';

import { getSiblingSites } from '../../config/sites';
import { useSite } from '../../site/SiteProvider';
import { ORDEN_ENTIDADES, colorPunto } from '../../config/rayos';
// 🔴 Musica no aparece en ninguna lista de secciones (Faina, 26-ago). Se llega
// por Spotify, aqui abajo, y desde dentro de cada canalizacion.
import { useModo } from '../../config/modo';
import logo from '../../assets/logo-footer.png';
import './footer.css';

/**
 * EL PIE.
 *
 * Cuatro columnas, y cada una responde a una pregunta distinta de quien llega
 * al final de una pagina: donde estoy · que mas hay · quien habla · como los
 * encuentro.
 *
 * 🔴 LO QUE SE ARREGLA RESPECTO DEL ANTERIOR:
 *
 *   1. Los enlaces llevaban a las secciones de la API, que hoy esta vacia: se
 *      caia en paginas en blanco. Ahora van al contenido propio.
 *   2. Los enlaces externos usaban <Link to> de react-router, que convierte una
 *      direccion de internet en una ruta interna y la rompe. Los externos van
 *      con <a href>, que es lo suyo.
 *   3. Los datos de contacto salen de sites.js, asi que cada dominio ensena los
 *      suyos — el .ong caia en los de Chile.
 *   4. Aparecen LOS TRES SITIOS. Quien entra por jeshua.cl no tiene forma de
 *      saber que existen .es y .ong si no se lo decimos.
 *   5. El aviso legal y la privacidad tienen su sitio: son OBLIGATORIOS antes
 *      de abrir en Europa, y el pie es donde el mundo entero los busca.
 *
 * El boton de DONAR vive aqui y no en la barra de arriba: una fundacion que
 * pide dinero en la primera linea de cada pagina se lee como una tienda.
 */

const Footer = () => {
  const { t } = useTranslation();
  const { modo } = useModo();
  const sitio = useSite();
  const hermanos = getSiblingSites();
  const anio = new Date().getFullYear();

  const SECCIONES = [
    { texto: t('menu.fundacion'), ruta: '/nosotros' },
    { texto: t('menu.tdl'), ruta: '/trabajadores-de-la-luz' },
    { texto: t('menu.esenios'), ruta: '/esenios' },
    { texto: t('menu.angeles'), ruta: '/angeles-y-arcangeles' },
    { texto: t('menu.biblioteca'), ruta: '/canalizaciones' },
    { texto: t('menu.gracias'), ruta: '/gracias' }
  ];

  const REDES = [
    sitio.whatsapp && sitio.whatsapp.url
      ? { icono: <WhatsAppIcon />, url: sitio.whatsapp.url, nombre: 'WhatsApp' } : null,
    { icono: <YouTubeIcon />, url: 'https://www.youtube.com/@jeshua.ben.joseph', nombre: 'YouTube' },
    { icono: <InstagramIcon />, url: 'https://www.instagram.com/jeshua.ben.joseph', nombre: 'Instagram' },
    { icono: <FacebookIcon />, url: 'https://www.facebook.com/jeshua.ben.joseph', nombre: 'Facebook' }
  ].filter(Boolean);

  return (
    <Box component="footer" className="pie">
      <Container maxWidth="lg">

        <Box className="pie-rejilla">

          {/* ① quién es esto — el logo y el botón, nada más (Faina, 26-ago).
              El nombre escrito sobraba: el logo ya lo lleva. */}
          <Box className="pie-col pie-col-marca">
            <Link to="/" className="pie-logo-enlace">
              <Box component="img" src={logo} alt={t('footer.nombre')} className="pie-logo" />
            </Link>
            <Link to="/nosotros" className="pie-donar">{t('footer.apoyar')}</Link>
          </Box>

          {/* ② qué más hay */}
          <Box className="pie-col">
            <Typography className="pie-titulo">{t('footer.sitio')}</Typography>
            {SECCIONES.map((s) => (
              <Link key={s.ruta} to={s.ruta} className="pie-enlace">{s.texto}</Link>
            ))}
          </Box>

          {/* ③ quién habla — el pie es un buen sitio para recordarlo */}
          <Box className="pie-col">
            {/* Centrado sobre sus dos columnas, no alineado a la izquierda como
                los demas titulos: aqui manda el bloque que tiene debajo, que es
                el doble de ancho (Faina, 26-ago). */}
            <Typography className="pie-titulo pie-titulo-centrado">{t('footer.voces')}</Typography>
            <Box className="pie-voces">
              {ORDEN_ENTIDADES.map((e) => (
                <Link
                  key={e}
                  to={`/canalizaciones?voz=${encodeURIComponent(e)}`}
                  className="pie-voz"
                  style={{ '--rayo': colorPunto(e, modo) }}
                >
                  <span className="pie-punto" />{e}
                </Link>
              ))}
            </Box>
          </Box>

          {/* ④ cómo encontrarnos */}
          <Box className="pie-col">
            <Typography className="pie-titulo">{t('footer.escribenos')}</Typography>
            <Link to="/contacto" className="pie-enlace pie-enlace-fuerte">{t('footer.contacto')}</Link>
            {/* El enlace «¿Algo no suena bien en tu idioma?» estuvo aqui del 15 al
                20-sep; Faina lo saco del pie. La puerta sigue en Contacto
                (motivo «idioma»). */}
            {sitio.email && (
              <a href={`mailto:${sitio.email}`} className="pie-enlace pie-correo">
                <MailIcon fontSize="inherit" /> {sitio.email}
              </a>
            )}
            {sitio.whatsapp && sitio.whatsapp.number && (
              <a href={sitio.whatsapp.url} target="_blank" rel="noopener noreferrer"
                 className="pie-enlace pie-correo">
                <WhatsAppIcon fontSize="inherit" /> {sitio.whatsapp.number}
              </a>
            )}
            {sitio.address && <Typography className="pie-dato">{sitio.address}</Typography>}

            <Box className="pie-redes">
              {REDES.map((r) => (
                <IconButton
                  key={r.nombre}
                  component="a"
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={r.nombre}
                  className="pie-red"
                >
                  {r.icono}
                </IconButton>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Los tres sitios */}
        {hermanos && hermanos.length > 0 && (
          <Box className="pie-hermanos">
            <span className="pie-hermanos-rotulo">{t('footer.tambien')}</span>
            {hermanos.map((h) => (
              <a key={h.domain} href={`https://${h.domain}`} className="pie-hermano">
                {h.domain}
              </a>
            ))}
          </Box>
        )}

        <Box className="pie-fondo">
          {/* Sin el RUT (Faina, 26-ago). Sigue en sites.js y va a la pagina de
              aviso legal, que es donde se busca; en el pie de cada pagina no
              hace falta. */}
          <Typography className="pie-legal">
            © {anio} {sitio.legalName || t('menu.nombre_fundacion')}
          </Typography>
          <Box className="pie-legal-enlaces">
            <Link to="/legal" className="pie-enlace-mini">{t('footer.legal')}</Link>
            <Link to="/privacidad" className="pie-enlace-mini">{t('footer.privacidad')}</Link>
            <Link to="/gracias" className="pie-enlace-mini">{t('footer.gracias')}</Link>
          </Box>
        </Box>

      </Container>
    </Box>
  );
};

export default Footer;
